import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { Text, Box, Flex, Menu, MenuList, MenuItem, MenuButton } from '@chakra-ui/react'
import cx from 'classnames'
import { injectIntl } from 'react-intl'
import { withRouter } from 'react-router-dom'

import Loader from 'src/components/Loader/Loader'
import { Layout, PageTitle, Content } from 'src/components/Layout'
import List from 'src/components/List/List'
import { isEmpty, logError } from 'src/utils/helpers'
import GroupRename from 'src/modules/Inventory/GroupRename/GroupRename.container'
import { WidgetContext } from 'src/modules/App/WidgetContext'
import intlMessagesApp from 'src/modules/App/translations'
import intlMessages from 'src/modules/Inventory/translations'
import FontAwesomeIconComponent from 'src/components/FontAwesomeIcon'
import styles from 'src/modules/Inventory/Groups/Groups.module.less'
import Button from 'src/components/Button/Button'
import DeleteConfirmation from 'src/modals/DeleteConfirmation/index'
import Export from 'src/modals/Export/index'
import withToast from 'src/components/Toast'

const focusStyle = {
  border: '2px solid',
  borderColor: 'black',
}

class Groups extends Component {
  state = {
    itemToExport: null,
    itemToDelete: null,
    itemToRename: null,
    activeGroupDropdown: false,
  }

  componentDidMount() {
    const { getInventoryGroups } = this.props
    getInventoryGroups()
  }

  componentDidUpdate(prevProps) {
    const {
      intl,
      getGroupsResponse,
      deleteGroupResponse,
      exportResponse,
      toast,
      getInventoryGroups,
    } = this.props

    // If there is a response and it changed (it's empty object while submitting)
    if (!isEmpty(getGroupsResponse) && getGroupsResponse !== prevProps.getGroupsResponse) {
      // If there was error getting record
      if (getGroupsResponse.success !== true) {
        toast({
          title: intl.formatMessage(intlMessagesApp.appErrorTitle),
          description:
            getGroupsResponse.message || intl.formatMessage(intlMessagesApp.appErrorDescription),
          status: 'error',
        })
      }
    }

    if (!isEmpty(deleteGroupResponse) && deleteGroupResponse !== prevProps.deleteGroupResponse) {
      if (deleteGroupResponse.success) {
        toast({
          title: intl.formatMessage(intlMessagesApp.appSuccessTitle),
          description: intl.formatMessage(intlMessages.inventoryGroupDeleteSuccess),
          status: 'success',
        })
        getInventoryGroups()

        this.setState({
          itemToDelete: null,
        })
      } else {
        this.setState({
          itemToDelete: null,
        })
        toast({
          title: intl.formatMessage(intlMessagesApp.appErrorTitle),
          description:
            deleteGroupResponse.message || intl.formatMessage(intlMessagesApp.appErrorDescription),
          status: 'error',
        })
      }
    }

    if (!isEmpty(exportResponse) && exportResponse !== prevProps.exportResponse) {
      if (exportResponse.success) {
        toast({
          title: 'Success',
          description: 'The exported data will be sent when it gets processed.',
          status: 'success',
        })

        this.setState({
          itemToExport: null,
        })
      } else {
        this.setState({
          itemToExport: null,
        })
        toast({
          title: intl.formatMessage(intlMessagesApp.appErrorTitle),
          description:
            exportResponse.message || intl.formatMessage(intlMessagesApp.appErrorDescription),
          status: 'error',
        })
      }
    }
  }

  addGroup = () => {
    const { history } = this.props
    history.push('/inventory/add')
  }

  viewGroup = item => {
    const { history } = this.props
    history.push('/inventory/view/' + item.listItemId)
  }

  /**
   * Confirm exporting specific Inventory Group (Room) or all groups
   *
   * @param groupId Group ID or null for all
   */
  exportDataConfirmation = (groupId = null) => {
    // Show export confirmation dialog
    this.setState({
      itemToExport: {
        id: groupId,
      },
    })
  }

  /**
   * Cancel export - closes export confirmation popup
   */
  exportDataClose = () => {
    this.setState({
      itemToExport: null,
    })
  }

  /**
   * Execute exporting specific Inventory Group (Room) or all groups
   */
  exportData = (groupId = null) => {
    const { inventoryExport, user } = this.props

    inventoryExport({
      type: 'zip',
      recipientEmail: user.email,
      recipientName: user.name,
      groupId: groupId,
    })
  }

  /**
   * Handle clicking on group action - Export
   *
   * @param event
   * @param group Group to export
   */
  exportGroup = (event, group) => {
    try {
      // Prevent opening list item
      event.stopPropagation()
    } catch (e) {
      logError('', e, 0)
    }
    this.exportDataConfirmation(group.id)
  }

  renameGroup = (event, itemToRename) => {
    try {
      // Prevent opening list item
      event.stopPropagation()
    } catch (e) {
      logError('', e, 0)
    }
    // Show rename page
    this.setState({
      itemToRename: itemToRename,
    })
  }

  deleteGroup = (event, itemToDelete) => {
    try {
      // Prevent opening list item
      event.stopPropagation()
    } catch (e) {
      logError('', e, 0)
    }
    // Show delete confirmation dialog
    this.setState({
      itemToDelete: itemToDelete,
    })
  }

  getListItems = () => {
    const { intl, groups } = this.props

    const { isOpenDropdown } = this.state

    if (!(groups && groups.length > 0)) {
      return ''
    }

    return groups.map(group => {
      const description = group.hasOwnProperty('countItems')
        ? group.countItems + ' ' + intl.formatMessage(intlMessages.inventoryItems)
        : null
      return {
        listItemId: group.id,
        name: group.name,
        description: description,
        customRightIcon: () => (
          <Menu isLazy autoSelect={false}>
            <MenuButton
              isOpen={isOpenDropdown}
              onClose={() => {
                this.setState({ isOpenDropdown: false })
              }}
              onClick={e => {
                e.stopPropagation()
                this.setState({ isOpenDropdown: true })
              }}
              _focusVisible={focusStyle}
            >
              <Box className={styles.inventoryItemMenuToggle}>
                <FontAwesomeIconComponent icon={['far', 'ellipsis-vertical']} size='sm' />
              </Box>
            </MenuButton>
            <MenuList>
              <MenuItem _hover={{ bg: 'brand.menuItem' }} onClick={e => this.exportGroup(e, group)}>
                <button>{intl.formatMessage(intlMessagesApp.appGlobalExport)}</button>
              </MenuItem>
              <MenuItem _hover={{ bg: 'brand.menuItem' }} onClick={e => this.renameGroup(e, group)}>
                <button>{intl.formatMessage(intlMessagesApp.appGlobalRename)}</button>
              </MenuItem>
              <MenuItem _hover={{ bg: 'brand.menuItem' }} onClick={e => this.deleteGroup(e, group)}>
                <button>{intl.formatMessage(intlMessagesApp.appGlobalDelete)}</button>
              </MenuItem>
            </MenuList>
          </Menu>
        ),
      }
    })
  }

  handleBack = () => {
    const { history } = this.props

    history.push('/')
  }

  renderDeleteConfirmationContent = () => {
    const { intl } = this.props
    return <Text>{intl.formatMessage(intlMessages.inventoryGroupDeleteConfirmMessage)}</Text>
  }

  renderExportConfirmationContent = () => {
    const { intl, user } = this.props
    return (
      <Text>
        {intl.formatMessage(intlMessagesApp.appGlobalExportConfirmationMessage, {
          email: user.email,
        })}
      </Text>
    )
  }

  renderRenameRoomContent = () => {
    const { intl } = this.props

    return <Text>{intl.formatMessage(intlMessages.inventoryEnterNewNameBelow)}</Text>
  }

  renderRenameRoomForm = () => {
    const { itemToRename } = this.state
    return (
      <GroupRename group={itemToRename} onClose={() => this.setState({ itemToRename: null })} />
    )
  }

  render() {
    const {
      intl,
      isGetGroupsInProgress,
      groups,
      groupsItemsCount,
      groupsItemsValue,
      isExportInProgress,
      inventoryGroupDelete,
      isDeleteGroupInProgress,
      features,
      countryIsoCode,
    } = this.props

    const { itemToExport, itemToDelete, itemToRename } = this.state

    const foundedFeature = features.find(f => f.id == 'inventory')
    const title =
      foundedFeature && foundedFeature.title
        ? foundedFeature.title
        : intl.formatMessage(intlMessages.inventoryHeader)

    const countGroups = groups && groups.length > 0 ? groups.length : 0

    const getCurrencySymbolIcon = () => {
      const isUk = countryIsoCode && countryIsoCode.toLowerCase() === 'gb'
      return isUk ? 'circle-sterling' : 'circle-dollar'
    }

    const summaryItems = [
      {
        type: 'groups',
        icon: ['far', 'cube'],
        label: intl.formatMessage(intlMessages.inventoryRooms),
        value: countGroups,
      },
      {
        type: 'items',
        icon: ['far', 'loveseat'],
        label: intl.formatMessage(intlMessages.inventoryItems),
        value: groupsItemsCount,
      },
      {
        type: 'value',
        icon: ['far', getCurrencySymbolIcon()],
        label: intl.formatMessage(intlMessages.inventoryValue),
        value: groupsItemsValue,
      },
    ]

    return (
      <Layout>
        <Content>
          <PageTitle showBackMobile={true} title={title} />

          {isGetGroupsInProgress || isExportInProgress ? (
            <Loader styleVariation='staticWithHeight' />
          ) : (
            <div className={styles.summeryItemsContainer}>
              <div className={styles.summeryItems}>
                {summaryItems.map(summaryItem => {
                  const summaryItemClassNames = cx({
                    'kiosk--chakra-colors-card-background': true,
                    [styles.summeryItem]: true,
                  })
                  return [
                    <div key={summaryItem.type} className={summaryItemClassNames}>
                      <Box opacity={0.5} mr={'20px'}>
                        <FontAwesomeIconComponent
                          icon={summaryItem.icon}
                          size='sm'
                          type='card-font'
                        />
                      </Box>
                      <Box flex='1'>
                        <Text
                          variant='card'
                          fontWeight={500}
                          fontSize={{ lg: '20px', base: '18px' }}
                          className={styles.summeryItemLabel}
                        >
                          {summaryItem.label}
                        </Text>
                      </Box>
                      <Text
                        variant='card'
                        fontWeight={500}
                        fontSize={{ lg: '20px', base: '18px' }}
                        className={styles.summeryItemValue}
                      >
                        {summaryItem.value}
                      </Text>
                    </div>,
                  ]
                })}
              </div>
              {countGroups ? (
                <Flex mt={'20px'} align={'center'} justify={'space-between'}>
                  <Text fontSize={'16px'} fontWeight={500} variant='secondary'>
                    {intl.formatMessage(intlMessages.inventoryRooms)} ({countGroups})
                  </Text>
                  <button onClick={() => this.exportDataConfirmation()}>
                    <Text
                      className={styles.singleActionButton}
                      fontSize={'16px'}
                      fontWeight={500}
                      variant='accent'
                      size='small'
                    >
                      {intl.formatMessage(intlMessagesApp.appGlobalExportAll)}
                    </Text>
                  </button>
                </Flex>
              ) : null}
              {countGroups > 0 ? (
                <List
                  items={this.getListItems()}
                  onClick={this.viewGroup}
                  noMargin={true}
                  className={styles.list}
                  classNameListItem={styles.listItem}
                  classNameListItemName={styles.listItemName}
                />
              ) : null}
              <Flex mt={'30px'} justify='center'>
                <Box maxW={{ lg: '440px', base: '100%' }} w={'100%'}>
                  <Button isFullWidth size='lg' onClick={this.addGroup}>
                    <Box mr={'10px'}>
                      <FontAwesomeIconComponent icon={['fal', 'plus']} size='xs' type='white' />
                    </Box>
                    {intl.formatMessage(intlMessages.inventoryAddNew) +
                      ' ' +
                      intl.formatMessage(intlMessages.inventoryRoom).toLowerCase()}
                  </Button>
                </Box>
              </Flex>
            </div>
          )}

          <DeleteConfirmation
            icon={['far', 'trash-can']}
            heading={`${intl.formatMessage(intlMessagesApp.appGlobalDelete)}`}
            subheading={`${itemToDelete && itemToDelete.name}`}
            confirmText={'Delete'}
            visible={itemToDelete}
            loading={isDeleteGroupInProgress}
            onClose={() => this.setState({ itemToDelete: null })}
            renderContent={this.renderDeleteConfirmationContent()}
            onConfirm={() => inventoryGroupDelete(itemToDelete.id)}
          />

          <DeleteConfirmation
            icon={['far', 'pen-line']}
            heading={`${intl.formatMessage(intlMessagesApp.appGlobalRename)}`}
            subheading={`${itemToRename && itemToRename.name}`}
            confirmText={intl.formatMessage(intlMessagesApp.appGlobalRename)}
            visible={itemToRename}
            onClose={() => this.setState({ itemToRename: null })}
            renderContent={this.renderRenameRoomContent()}
            renderForm={this.renderRenameRoomForm()}
            hideActions={true}
          />

          <Export
            visible={itemToExport}
            onClose={() => this.setState({ itemToExport: null })}
            renderContent={this.renderExportConfirmationContent()}
            onConfirm={() => this.exportData(itemToExport.id)}
          />
        </Content>
      </Layout>
    )
  }
}

Groups.propTypes = {
  isGetGroupsInProgress: PropTypes.bool.isRequired,
  getGroupsResponse: PropTypes.object.isRequired,
  groups: PropTypes.array.isRequired,
  groupsItemsCount: PropTypes.string.isRequired,
  groupsItemsValue: PropTypes.string.isRequired,
  deleteGroupResponse: PropTypes.object.isRequired,
  isExportInProgress: PropTypes.bool.isRequired,
  exportResponse: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  getInventoryGroups: PropTypes.func.isRequired,
  inventoryGroupDelete: PropTypes.func.isRequired,
  inventoryExport: PropTypes.func.isRequired,
  intl: PropTypes.object,
  router: PropTypes.object,
}

const GroupsContext = props => (
  <WidgetContext.Consumer>{context => <Groups {...props} {...context} />}</WidgetContext.Consumer>
)

export default withRouter(injectIntl(withToast(GroupsContext)))
